import { map, catchError, switchMap, withLatestFrom, filter, tap } from 'rxjs/operators';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { toObservable } from '@angular/core/rxjs-interop';
import { INotificationOptions } from '@aston/foundation';
import { Injectable, inject } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { mapToDunningInterestPenaltyStatementModel, selectedAdditionalDocuments } from '../../../dunnings-module/functions';
import { DunningTimelineStore } from '../../../dunnings-module/stores/dunning-timeline/dunning-timeline.store';
import { SharedTemplatesStore } from '../../../shared-module/stores/shared-templates.store';
import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import { CorrelationParams, aggregate } from '../../models/correlated-actions.model';
import { DunningActionsService } from '../../../dunnings-module/services';
import { IDunningActionForm } from '../../../dunnings-module/models';
import { WebSocketService } from '../../../shared-module/services';
import { ActionModalPanel } from '../../../dunnings-module/enums';
import * as AppStoreActions from '../../app-store/actions';
import { WsEventType } from '../../../shared-module/enums';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';


@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreEditionEffects {

	private dunningActionsService = inject(DunningActionsService);
	private templatesStore = inject(SharedTemplatesStore)
	private tlStore = inject(DunningTimelineStore);
	private actions$ = inject(Actions);
	private store = inject(Store);
	private ws = inject(WebSocketService);

	payingCenterModeEnabled$ = toObservable(inject(SuperDebtorPageStore).payingCenterModeEnabled)
	stdDunningCase$ = toObservable(inject(SuperDebtorPageStore).dunningCaseSTD)

	initFormPanelsSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenForm, featureActions.OpenDuplicationForm),
		// Create an aggregated action listener
		// it will observe the race of all specified observables
		// and complete when all these actions are dispatched.
		// Note that individual actions workflow still apply.
		aggregate(
			this.actions$.pipe(ofType(featureActions.LoadDunningActionSuccess)),
			// other panel data success actions types
			this.actions$.pipe(ofType(featureActions.LoadAssignableUsersSuccess)),
			this.actions$.pipe(ofType(featureActions.LoadAllUsersSuccess)),
			this.actions$.pipe(ofType(featureActions.LoadCommentsSuccess)),
			this.actions$.pipe(ofType(featureActions.InitFailure)),
		),
		switchMap((args: any[]) => this.store.select(featureSelectors.selectSuperDebtorId).pipe(map(sid => [sid, ...args]))),
		switchMap(([superDebtorId, dunningActionSuccess]) => {
			const dunningLevel = dunningActionSuccess.entity.dunningLevel;

			// if already realized, the accounting are loaded based on the current selection of the DA.
			const toRealize = !dunningActionSuccess.entity.realizationDate;

			// // dont reload contacts if already present on the same SD
			// state.dunningActionDetail.contacts.entity.length > 0 && (state.dunningActionDetail.superDebtorId === action.superDebtorId) ?
			// 	featureActions.LoadContactsSuccess({entity: state.dunningActionDetail.contacts.entity, correlationParams}) :
			// 	featureActions.LoadContactsRequest(correlationParams),

			return [
				// here we can initialize the whole popin form
				// with every loaded components
				featureActions.InitSuccess({
					superDebtorId,
					dunningAction: dunningActionSuccess.entity,
					attachmentDocumentFiles: [],
					linkedDocumentIds: dunningActionSuccess.entity.linkedDocumentIds,
					linkedAccountingDocumentIds: dunningActionSuccess.entity.accounting,
				}),

				// Task 24732: [MO] Front / Modale DA Réalisation / Service+NGRX Infos postales
				toRealize ? AppStoreActions.Noop() : featureActions.LoadDunningActionSendingStatusRequest(dunningActionSuccess.entity.id),

				toRealize ? featureActions.LoadContactsRequest() : featureActions.LoadSelectedContactsRequest(),

				// accounting
				dunningLevel && toRealize ? featureActions.LoadAccountingRequest(dunningLevel) : AppStoreActions.Noop(),

				// attachments
				featureActions.LoadAttachmentsRequest(dunningActionSuccess.entity.id),

				// refresh selected accounting documents
				featureActions.LoadSelectedAccountingRequest()
			];
		}),
		catchError(error => of(featureActions.InitFailure({error})))
	));

	initFormPanelsRequests$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenForm, featureActions.OpenDuplicationForm),
		withLatestFrom(
			this.payingCenterModeEnabled$,
			this.store.select(featureSelectors.selectAssignableUsers),
			this.store.select(featureSelectors.selectSuperAdminUsers),
			this.store.select(featureSelectors.selectAllUsers)),
		switchMap(([action, isPC, assignableUsers, superAdminUsers, allUsers]) => {
			const correlationParams: CorrelationParams = action.correlationParams;
			correlationParams.parentActionType = action.type;

			const isEditMode = !!action.action && !!action.action.id;
			const isDuplicateMode = action.type === featureActions.OpenDuplicationForm.type;

			return [
				featureActions.SetEditFormMode({param: isEditMode && !isDuplicateMode}),

				// handle action creation
				isDuplicateMode ?
					featureActions.LoadDunningActionToDuplicateRequest(action.action.id, correlationParams) :
					isEditMode ?
						featureActions.LoadDunningActionRequest(action.action.id, correlationParams) :
						featureActions.LoadDunningActionSuccess({entity: action.action as IDunningActionForm, correlationParams}),

				// other panel data request actions

				// wont reload templates if already present
				AppStoreActions.Execute(() => this.templatesStore.loadAllTemplates()),

				// dont reload users if already present
				assignableUsers.length > 0 ?
					featureActions.LoadAssignableUsersSuccess({ entity: assignableUsers, correlationParams }) :
					featureActions.LoadAssignableUsersRequest(correlationParams),

				// reload assignable operators
				isPC ? featureActions.LoadAssignablesOperatorsRequest({ param: action.superDebtorId }) : null,

				// reload notifiables
				isPC ? featureActions.LoadNotifiablesUsersRequest({ param: action.superDebtorId }) : null,

				// dont reload users if already present
				superAdminUsers.length > 0 ?
					featureActions.LoadSuperAdminUsersSuccess({ entity: superAdminUsers, correlationParams }) :
					featureActions.LoadSuperAdminUsersRequest(correlationParams),

				// dont reload users if already present
				allUsers.length > 0 ?
					featureActions.LoadAllUsersSuccess({entity: allUsers, correlationParams}) :
					featureActions.LoadAllUsersRequest(correlationParams),

				// handle comments
				(isEditMode && !isDuplicateMode) ?
					featureActions.LoadCommentsRequest(action.action.id, correlationParams) :
					featureActions.LoadCommentsSuccess({entity: [], correlationParams}),
			].filter(Boolean);
		}),
		catchError(error => of(featureActions.InitFailure({error})))
	));

	loadActionRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.LoadDunningActionRequest),
		switchMap(action => this.dunningActionsService.getActionForm(action.id).pipe(
			map(model => featureActions.LoadDunningActionSuccess({entity: model, correlationParams: action.correlationParams})), // LoadDunningActionSuccess should be aggregatable
			catchError(error => of(featureActions.LoadDunningActionFailure({error})))
		))
	));

	updateAccountingForDunningLevelEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSelectedLevel),
		map(action => action.param ?
			featureActions.LoadAccountingRequest(action.param) :
			featureActions.LoadAccountingSuccess({list: {items: []}})
		)
	));

	updateDocumentsForDunningLevelEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateSelectedLevel),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectActionsSpecs),
		),
		switchMap(([_, dunningAction, currentAttachments, specs]) => {
			let attachments = currentAttachments.slice()
			const actions: Action[] = [];
			// Some documents are valid for specific dunning levels only

			// Interest & penalties
			// Bug 38585: Les pièces jointes affichées ne correspondent pas aux toggles activés
			// Bug 38584: Le relevé des I&P est affiché alors que le NDR est != STD
			attachments = attachments.filter(a => !a.isInterestPenaltyStatement).concat(
				specs[dunningAction.type].hasInterestPenaltyStatementPlaceholder(dunningAction)
					? mapToDunningInterestPenaltyStatementModel()
					: []
			);

			actions.push(featureActions.LoadAttachmentsSuccess({entity: attachments}))
			console.debug(`[action-form] reload attachments after level changed`, attachments)
			return actions
		})
	));

	// Dispatch an intermediate action on form submit (CREATE)
	submitFormToCreateEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitForm),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectSuperDebtorId)),
		filter(([, dunningAction]) => !dunningAction.id),
		switchMap(([action, dunningAction, attachments, superDebtorId]) => {
			// Actions on submit
			return [
				featureActions.CreateDunningActionRequest({
					superDebtorId,
					dunningAction,
					attachmentDocumentFiles: attachments.filter(a => !a.id && a.file).map(a => a.file), // CHECK
					linkedAccountingDocumentIds: dunningAction.accounting,
					linkedDocumentIds: selectedAdditionalDocuments(attachments).map(a => a.document.id),
				}, action.param)
			];
		})
	));

	// Dispatch an intermediate action on form submit (UPDATE)
	submitFormToUpdateEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SubmitForm),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectSuperDebtorId)),
		filter(([, dunningAction]) => !!dunningAction.id),
		tap(_ => this.ws.emitLocally(WsEventType.RefreshBoardTasks)),
		switchMap(([action, dunningAction, attachments, superDebtorId]) => {
			// Actions on submit
			return [
				featureActions.UpdateDunningActionRequest({
					superDebtorId,
					dunningAction,
					attachmentDocumentFiles: attachments.filter(a => !a.id && a.file).map(a => a.file),
					linkedAccountingDocumentIds: dunningAction.accounting,
					linkedDocumentIds: selectedAdditionalDocuments(attachments).map(a => a.document.id),
				}, action.param)
			];
		})
	));

	createActionRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CreateDunningActionRequest),
		withLatestFrom(
			this.stdDunningCase$,
			this.store.select(featureSelectors.selectSuperDebtorId)),
		switchMap(([action, std, superDebtorId]) => {
			action.entity.dunningAction.dunningCaseId = std.id;
			return this.dunningActionsService.createAction(action.entity).pipe(
				switchMap(id => [
					featureActions.CreateDunningActionSuccess({
						entity: {
							...action.entity,
							dunningAction: {
								...action.entity.dunningAction,
								id
							}
						}
					}),
					action.chainWithCompletion ?
						featureActions.InitCompletionFormRequest(superDebtorId, id) :
						AppStoreActions.Noop()
				]),
				catchError(error =>
					of(featureActions.CreateDunningActionFailure({error}))
				));
		})
	));

	createDunningActionSuccessEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CreateDunningActionSuccess),
		withLatestFrom(this.store),
		switchMap(([action]) => {
			return [
				featureActions.CloseForm(),
				AppStoreActions.ToastSuccess('Dunning.Actions.Messages.CreatedLong', null, {
					data: {
						action_id: action.entity.dunningAction.id,
						case_id: action.entity.dunningAction.dunningCaseId
					}
				}),
				AppStoreActions.Execute(() => this.tlStore.loadTimeline()),
			];
		})));

	updateActionRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateDunningActionRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectIsCompletionMode),
			this.store.select(featureSelectors.selectSuperDebtorId)),
		filter(([, isCompletionMode]) => !isCompletionMode),
		switchMap(([action, _, superDebtorId]) => {
			return this.dunningActionsService.updateAction(action.entity).pipe(
				switchMap(_ => [
					featureActions.UpdateDunningActionSuccess({entity: action.entity}),
					action.chainWithCompletion ?
						featureActions.InitCompletionFormRequest(superDebtorId, action.entity.dunningAction.id) :
						AppStoreActions.Noop()
				]),
				catchError(error =>
					of(featureActions.UpdateDunningActionFailure({error}))
				));
		})
	));

	updateDunningActionSuccessEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.UpdateDunningActionSuccess),
		withLatestFrom(this.store),
		switchMap(() => [
			featureActions.CloseForm(),
			AppStoreActions.Execute(() => this.tlStore.loadTimeline()),
		])));

	cancelFormEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CancelForm),
		withLatestFrom(
			this.store.select(featureSelectors.selectDunningAction),
			this.store.select(featureSelectors.selectAttachments),
			this.store.select(featureSelectors.selectSelectedAccountingList),
			this.store.select(featureSelectors.selectSuperDebtorId),
		),
		switchMap(([action, dunningAction, attachments, selectedAccountingDocs, superDebtorId]) => {
			const cancelOptions: INotificationOptions = !action.formIsDirty ? null : {
				autoCloseDelay: 10000,
				undoText: 'Toasts.Redo',
				undoAction: () => this.store.dispatch(featureActions.ReOpenForm({
					dunningAction,
					superDebtorId,
					attachmentDocumentFiles: attachments.filter(a => !a.id && a.file).map(a => a.file),
					linkedAccountingDocumentIds: selectedAccountingDocs.items.map(a => a.accountingDocumentId),
					linkedDocumentIds: selectedAdditionalDocuments(attachments).map(a => a.document.id),
				}, ActionModalPanel.Form))
			};
			return [
				AppStoreActions.ToastReset(),
				AppStoreActions.ToastWarning('Toasts.CancelledForm', cancelOptions)
			];
		})
	));
}
